// src/pages/ManualMarketing/ManualMarketingGallery/ChannelSelectSheet.jsx
import React, { useMemo, useRef, useState, useEffect, useLayoutEffect  } from "react";
import { BottomWithButton } from "../../../../assets/components/BottomSheetGroup";
import { SplitRowQuarter } from "../../../../assets/components/ButtonGroup";
import { Chip } from "../../../../assets/components/Chip";
import HardCodeImage from "../../../../assets/icons/ChannelSelectImage/HardCodeImage.png";
import info from "../../../../assets/icons/System/info.svg";

const CHANNEL_SHEET_OPTIONS = [
  "인스타 스토리",
  "인스타 게시물",
  "카카오톡",
  "네이버 밴드",
  "X(트위터)",
  "MMS(문자)"
];

const AI_CODE_TO_LABEL = {
  "1": "카카오톡",
  "2": "인스타 스토리",
  "3": "인스타 게시물",
  "5": "MMS(문자)",
  "6": "네이버 밴드",
  "7": "X(트위터)"
};

const normalize = (s = "") =>
  s.replace(/\s+/g, "").replace(/그램/g, "").toLowerCase();

const LABEL_ALIASES = {
  인스타피드: "인스타 게시물",
  인스타그램피드: "인스타 게시물",
  "네이버 밴드": "네이버 밴드",
};

const sameLabel = (a, b) =>
  (LABEL_ALIASES[normalize(a)] || normalize(a)) ===
  (LABEL_ALIASES[normalize(b)] || normalize(b));

const getCropAspect = (label) =>
  !label
    ? null
    : sameLabel(label, "카카오톡") || sameLabel(label, "인스타 스토리") || sameLabel(label, "MMS(문자)")
      ? 9 / 16
      : sameLabel(label, "인스타 게시물") || sameLabel(label, "네이버 밴드") || sameLabel(label, "X(트위터)")
        ? 1
        : null;

export default function ChannelSelectSheet({
  isOpen,
  onClose,
  value,
  onSelect,
  onConfirm,
  height,
}) {
  const previewSrc = HardCodeImage;
  const options = CHANNEL_SHEET_OPTIONS;

  // 로컬스토리지 AI 채널 추천
  const aiChannelCode = useMemo(() => {
    try {
      return typeof window === "undefined"
        ? null
        : window.localStorage.getItem("aiChannel") || null;
    } catch {
      return null;
    }
  }, []);

  const aiRecommendedLabel = aiChannelCode
    ? AI_CODE_TO_LABEL[aiChannelCode]
    : null;

  const [selected, setSelected] = useState("");

  // 열릴 때: value 우선 → 없으면 AI 추천 → 없으면 초기화
  useEffect(() => {
    if (!isOpen) return;

    // 1) 현재 value를 options 중 하나로 매핑
    if (value) {
      const hit = options.find((o) => sameLabel(o, value));
      if (hit) {
        setSelected(hit);
        return;
      }
    }

    // 2) AI 추천 라벨을 options 중 하나로 매핑
    if (aiRecommendedLabel) {
      const reco = options.find((o) => sameLabel(o, aiRecommendedLabel));
      if (reco) {
        setSelected(reco);
        return;
      }
    }

    // 3) 초기화
    setSelected("");
  }, [isOpen, options, value, aiRecommendedLabel]);

  // ★ 고정 프레임 크기

  const FRAME_H = 410;
  const frameRef = useRef(null);
  const [frameW, setFrameW] = useState(360);

  // ✅ 실제 프레임 폭(= 화면에서 보이는 폭) 기준으로 crop 계산
  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = frameRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth || 0;
      setFrameW(Math.min(360, w)); // 최대 360, 좁으면 자동 축소
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);


  // 선택 채널 비율에 맞는 중앙 뷰포트 크기 계산
  const cropAspect = getCropAspect(selected); // 9/16 or 1
  const cropRect = useMemo(() => {
    if (!cropAspect) return { w: frameW , h: FRAME_H };
    if (frameW / FRAME_H < cropAspect) {
      return { w: frameW, h: Math.round(frameW / cropAspect) };
    }
    return { w: Math.round(FRAME_H * cropAspect), h: FRAME_H };
  }, [cropAspect, frameW]);

  const ratioText =
    cropAspect === 9 / 16 ? "9:16" : cropAspect === 1 ? "1:1" : "";

  // 가로 드래그 스크롤
  const containerRef = useRef(null);
  const chipRefs = useRef({}); // 각 채널 칩 DOM 저장
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  const onDown = (e) => {
    if (!containerRef.current) return;
    isDraggingRef.current = true;
    startXRef.current = e.pageX - containerRef.current.offsetLeft;
    scrollLeftRef.current = containerRef.current.scrollLeft;
  };
  const onLeave = () => {
    isDraggingRef.current = false;
  };
  const onUp = () => {
    isDraggingRef.current = false;
  };
  const onMove = (e) => {
    if (!isDraggingRef.current || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    containerRef.current.scrollLeft =
      scrollLeftRef.current - (x - startXRef.current);
  };

  // ✅ 시트 열릴 때 + 선택 변경 시, 해당 칩이 가운데로 오도록 스크롤
  useEffect(() => {
    if (!isOpen || !containerRef.current || !selected) return;

    const container = containerRef.current;

    // selected가 alias일 수도 있으니 options에서 실제 키를 다시 찾음
    const key = options.find((o) => sameLabel(o, selected)) || selected;
    const targetEl = chipRefs.current[key];
    if (!targetEl) return;

    const chipLeft = targetEl.offsetLeft;
    const chipWidth = targetEl.offsetWidth;
    const containerWidth = container.clientWidth;

    const scrollLeft = Math.max(
      chipLeft - (containerWidth - chipWidth) / 2,
      0
    );

    container.scrollTo({ left: scrollLeft, behavior: "smooth" });
  }, [isOpen, selected, options]);

  const handleClose = () => onClose && onClose();
  const handleSave = () => {
    if (!selected) return;
    (onConfirm ? onConfirm : onSelect)?.(selected);
    onClose?.();
  };

  return (
    <BottomWithButton
      isOpen={isOpen}
      onClose={handleClose}
      title="홍보 채널"
      subtitle="어떤 채널에 홍보하고 싶나요?"
      height={height}
      onSubmit={handleSave}
      buttonLabel="저장"
      buttonDisabled={!selected}
      renderButton={({ onClick, disabled }) => (
        <SplitRowQuarter
          leftText="닫기"
          onLeft={handleClose}
          rightType="fill"
          rightText="저장"
          onRight={onClick}
          rightDisabled={disabled}
        />
      )}
    >
      {/* 시트 내용 */}
      <div className="flex flex-col h-full">
        {/* 채널 칩 리스트 */}
        <div>
          <div
            ref={containerRef}
            className="flex overflow-x-auto gap-3 no-scrollbar cursor-grab active:cursor-grabbing pb-[16px]"
            onMouseDown={onDown}
            onMouseLeave={onLeave}
            onMouseUp={onUp}
            onMouseMove={onMove}
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {options.map((opt) => {
              const isSelected =
                selected && sameLabel(selected, opt); // ✅ 정확히 이 칩이 선택인지
              const isReco =
                aiRecommendedLabel && sameLabel(opt, aiRecommendedLabel); // ✅ AI 추천 칩만 배지

              return (
                <div key={opt} className="flex-shrink-0"
                  ref={(el) => {
                    if (el) chipRefs.current[opt] = el;
                  }}
                >
                  <Chip
                    chip="a"
                    status={isSelected ? "selected" : "default"}
                    label={opt}
                    badgeLabel={isReco ? "AI 추천" : undefined}
                    allowBadge
                    onClick={() => setSelected(opt)}
                  />
                </div>
              );
            })}
            <div className="shrink-0 w-[1px]" />
          </div>
        </div>

        {/* 프리뷰 */}
        <div
          ref={frameRef}
          className="w-full max-w-[360px] rounded-[24px] mx-auto bg-surface-light-subtle"
          style={{ height: FRAME_H }}
        >
          <div
            className="w-full relative mx-auto rounded-[24px] overflow-hidden bg-[var(--coolgrey-25)]"
            style={{ height: FRAME_H }}
          >
            {!!ratioText && (
              <Chip
                chip="e"
                className="absolute left-1/2 -translate-x-1/2 top-8 z-10"
                label={ratioText}
              />
              // <div className="absolute left-1/2 w-14 -translate-x-1/2 text-center top-8 px-3 py-1 rounded-full bg-black/70 text-white text-caption border border-white-1 z-10">
              //   {ratioText}
              // </div>
            )}

            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[18px]"
              style={{
                width: cropRect.w,
                height: cropRect.h,
                background: "#000",
              }}
            >
              <img
                src={previewSrc}
                alt="preview"
                className="block w-full h-full rounded-xl"
                style={{ objectFit: "cover", objectPosition: "center" }}
              />
            </div>
          </div>
        </div>
        <div className="pt-[16px] flex items-center text-b1 text-text-subtler">
          <img src={info} alt="" />
          <span>보이는 비율로 이미지가 생성됩니다.</span>
        </div>
      </div>
    </BottomWithButton>
  );
}
